import { useMemo } from 'react'
import { useInView } from 'react-intersection-observer'
import styled from 'styled-components'
import { rem } from 'polished'

import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { Experiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import useOfferPropertySurroundings from 'hooks/Offers/useOfferPropertySurroundings'
import OfferPageHighlightTagItem from './OfferPageHighlightTagItem'
import useOfferPageHighlightTags, {
  shouldFetchOfferPageHighlightSurroundings,
  type OfferPageHighlightTagContext,
} from './useOfferPageHighlightTags'
import { getOfferPageHighlightTravellerContext } from './offerPageHighlightTagsUtils'

const HighlightGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

const ActivationSentinel = styled.div`
  height: 1px;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  width: 1px;
`

interface Props {
  context?: OfferPageHighlightTagContext
  contextBuilder?: () => OfferPageHighlightTagContext
  duration?: number
  hasSelectedDates: boolean
  isOccupancyContextKnown: boolean
  offerPageHighlightTagsOverride?: Array<App.OfferPageHighlightTagOverride>
  propertyId: string
  rooms?: Array<App.Occupants>
}

interface PropsWithSurroundings extends Props {
  surroundingsOfferId: string
}

function useOfferPageHighlightTagsExperimentActivation() {
  const [activationRef, inView] = useInView({
    triggerOnce: true,
  })
  const { isAnyVariant } = useExperiment(Experiments.croOfferPageHighlightTagsV2, {
    canActivate: inView,
  })

  return {
    activationRef,
    isAnyVariant,
  }
}

/**
 * Tag overrides own the rendered tags, but heading copy still needs holiday-type signals
 * (e.g. Romantic Escape requires Romantic/Honeymoon). Keep a minimal context in that path.
 */
function resolveOfferPageHighlightTagContext(
  providedContext: OfferPageHighlightTagContext | undefined,
  contextBuilder: (() => OfferPageHighlightTagContext) | undefined,
  hasTagOverrides: boolean,
): OfferPageHighlightTagContext | undefined {
  if (!hasTagOverrides) {
    return contextBuilder?.() ?? providedContext
  }

  const builtContext = contextBuilder?.() ?? providedContext
  const holidayTypes = builtContext?.holidayTypes ?? providedContext?.holidayTypes
  if (!holidayTypes?.length) {
    return providedContext
  }

  return {
    ...providedContext,
    holidayTypes,
  }
}

function OfferPageHighlightTags(props: Props) {
  const { activationRef, isAnyVariant } = useOfferPageHighlightTagsExperimentActivation()

  return <>
    <ActivationSentinel ref={activationRef} aria-hidden="true" data-testid="offer-page-highlight-tags-activation-sentinel" />
    {isAnyVariant && <OfferPageHighlightTagsContent {...props} />}
  </>
}

export function OfferPageHighlightTagsWithSurroundings(props: PropsWithSurroundings) {
  const { activationRef, isAnyVariant } = useOfferPageHighlightTagsExperimentActivation()

  return <>
    <ActivationSentinel ref={activationRef} aria-hidden="true" data-testid="offer-page-highlight-tags-activation-sentinel" />
    {isAnyVariant && <OfferPageHighlightTagsSurroundingsContent {...props} />}
  </>
}

function OfferPageHighlightTagsSurroundingsContent(props: PropsWithSurroundings) {
  const {
    context: providedContext,
    contextBuilder,
    isOccupancyContextKnown,
    offerPageHighlightTagsOverride,
    rooms,
    surroundingsOfferId,
    ...contentProps
  } = props
  const baseContext = useMemo(
    () => resolveOfferPageHighlightTagContext(
      providedContext,
      contextBuilder,
      !!offerPageHighlightTagsOverride?.length,
    ),
    [contextBuilder, offerPageHighlightTagsOverride, providedContext],
  )
  const surroundingsParams = useMemo(
    () => ({ offerId: surroundingsOfferId }),
    [surroundingsOfferId],
  )
  const travellerContext = getOfferPageHighlightTravellerContext(rooms, isOccupancyContextKnown)
  const shouldFetchSurroundings = useMemo(
    () => !offerPageHighlightTagsOverride?.length && shouldFetchOfferPageHighlightSurroundings(baseContext, travellerContext, rooms),
    [baseContext, offerPageHighlightTagsOverride, rooms, travellerContext],
  )
  const { error, hasFetched, surroundings } = useOfferPropertySurroundings(
    surroundingsParams,
    { enabled: shouldFetchSurroundings },
  )
  const context = useMemo<OfferPageHighlightTagContext | undefined>(
    () => {
      if ((!hasFetched && !error) || !surroundings) return baseContext

      return {
        ...baseContext,
        surroundings,
      }
    },
    [baseContext, error, hasFetched, surroundings],
  )

  return <OfferPageHighlightTagsContent
    {...contentProps}
    context={context}
    isOccupancyContextKnown={isOccupancyContextKnown}
    offerPageHighlightTagsOverride={offerPageHighlightTagsOverride}
    rooms={rooms}
  />
}

function OfferPageHighlightTagsContent(props: Props) {
  const {
    context: providedContext,
    contextBuilder,
    duration,
    hasSelectedDates,
    isOccupancyContextKnown,
    offerPageHighlightTagsOverride,
    propertyId,
    rooms,
  } = props
  const context = useMemo(
    () => resolveOfferPageHighlightTagContext(
      providedContext,
      contextBuilder,
      !!offerPageHighlightTagsOverride?.length,
    ),
    [contextBuilder, offerPageHighlightTagsOverride, providedContext],
  )
  const highlightTagProps = useMemo(
    () => ({
      context,
      duration,
      hasSelectedDates,
      isOccupancyContextKnown,
      offerPageHighlightTagsOverride,
      propertyId,
      rooms,
    }),
    [context, duration, hasSelectedDates, isOccupancyContextKnown, offerPageHighlightTagsOverride, propertyId, rooms],
  )
  const { title, tags } = useOfferPageHighlightTags(highlightTagProps)

  return tags.length > 0 && (
    <Group direction="vertical" gap={16} data-testid="offer-page-highlight-tags">
      <Heading variant="heading3" as="h2">{title}</Heading>
      <HighlightGrid>
        {tags.map(tag => <OfferPageHighlightTagItem key={tag.id} tag={tag} />)}
      </HighlightGrid>
    </Group>
  )
}

export default OfferPageHighlightTags
